import { Observable } from "@babylonjs/core/Misc/observable";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreatePlaneVertexData } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { HEX_COLOR_RE } from "./bindingValues";
import { Material } from "@babylonjs/core/Materials/material";
import type { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { TraceBehavior } from "./tracebehavior";
import type { ValidationError } from "../scenedata/behaviordata";
import { BehaviorData } from "../scenedata/behaviordata";
import { Orientation } from "../scenedata/common";
import { generateRoundedQuadGeometry } from "./bevel";

// 2024
// Author: Aaron Post

/**
 * Append back faces to single-sided quad geometry: the same vertices
 * duplicated with negated normals, indexed in reverse winding. The back
 * shows the same UV mapping, so a picture reads mirrored from behind - a
 * print seen through its paper. Geometry rather than a material culling
 * flag for the reason applyQuadShape gives: Renderer overrides bring their
 * own materials, and a flag on ours would not survive them.
 */
function withBackFaces(data: VertexData): VertexData {
    const pos = Array.from(data.positions ?? []);
    const nrm = Array.from(data.normals ?? []);
    const uv = Array.from(data.uvs ?? []);
    const idx = Array.from(data.indices ?? []);
    const base = pos.length / 3;
    data.positions = pos.concat(pos);
    data.normals = nrm.concat(nrm.map((n) => -n));
    data.uvs = uv.concat(uv);
    const back: number[] = [];
    for (let i = 0; i + 2 < idx.length; i += 3) {
        back.push(base + idx[i], base + idx[i + 2], base + idx[i + 1]);
    }
    data.indices = idx.concat(back);
    return data;
}

/**
 * Abstract base class for graphic behaviors (images, videos)
 */
export abstract class Graphic<TData extends BehaviorData = BehaviorData> extends TraceBehavior<TData> {
    public abstract aspectRatio: number;
    public abstract orientation: Orientation;

    /** Fires when aspectRatio changes (e.g. after media metadata loads). */
    public onAspectChangedObservable = new Observable<number>();

    /** Fires when orientation changes (e.g. via commit). */
    public onOrientationChangedObservable = new Observable<Orientation>();

    /** Fires after each re-fit of an existing quad: new media, fit mode, orientation or box. */
    public onFitChangedObservable = new Observable<void>();

    /**
     * Whether `aspectRatio` describes the real media yet.
     *
     * `aspectRatio` has to hold a number from the moment the behavior exists,
     * so it starts at a placeholder. A placeholder is NOT an aspect: until the
     * media (or the authored data) says otherwise, the only shape anyone has
     * committed to is the node's own box. Subclasses set this the moment they
     * have a real number — see updateQuadFit for what it changes.
     */
    protected aspectKnown = false;

    /** Node scale the current quad fit was computed against. */
    private _fittedScale: Vector3 | null = null;
    private _fitObserver: Observer<TransformNode> | null = null;

    /** The media quad to fit, when it exists. */
    protected abstract getFitQuad(): Mesh | null;

    /** The authored fit mode updateQuadFit applies (data `fit`; absent = contain). */
    protected abstract getFitMode(): 'contain' | 'cover';

    /** The authored corner radius updateQuadFit realizes (data `cornerRadius`, meters; absent = 0). */
    protected abstract getCornerRadius(): number;

    /**
     * Whether the authored shape is a capsule (data `pill`).
     *
     * Reported as intent rather than a radius because half the smaller side
     * is only knowable once the quad has been fitted — `applyQuadShape`
     * resolves it against the realized width and height (#759).
     */
    protected abstract getPill(): boolean;

    /** What the quad's back face shows - subclasses read their authored data. */
    protected abstract getBack(): 'none' | 'picture' | 'color';
    /** The card color when getBack() is 'color' (#RRGGBB(AA)). */
    protected abstract getBackColor(): string;

    /** Hook for subclasses that lay out companions against the fitted rect. */
    protected onQuadFitUpdated(_fw: number, _fh: number): void { /* optional */ }

    /**
     * The visible media rect as fractions of the node's box, in the node's
     * local axes: `contain` shrinks one axis; `cover`, an unknown aspect and a
     * quad not built yet are (1, 1).
     */
    public getFittedRect(): { sx: number; sy: number } {
        const fitted = this.getFitQuad()?.scaling;
        return { sx: fitted?.x ?? 1, sy: fitted?.y ?? 1 };
    }

    /**
     * Fit the quad to the authored box. The node's (possibly
     * non-uniform) scale defines the box; comparing the media aspect against
     * the box aspect letterboxes without distortion. Comparing against the
     * unit square instead is only correct for square boxes — a 16:9 image in
     * a 3:1 strip rendered stretched. The quad's local scaling divides out
     * the mismatch, so node scale x quad scale always reproduces the media
     * aspect. The box is ALWAYS the node's own scale — whoever wrote it
     * (layout solve, gizmo resize, undo); watchNodeScaleForFit re-fits
     * whenever it changes, so media behaviors need no knowledge of layout.
     *
     * That is `contain`, the default. `cover` (getFitMode) is the
     * complement: the quad spans the box exactly and the same per-axis
     * mismatch moves into the UV window instead — the overflow is cropped
     * off-screen (centered), still never distorted. An authored corner
     * radius (getCornerRadius) rounds the realized rectangle — the
     * letterboxed rect under contain, the box under cover. Both land in
     * applyQuadShape; see it for why crop and rounding live in the quad's
     * geometry.
     *
     * With no aspect known the quad FILLS the box instead, under either
     * mode with no crop. Letterboxing needs
     * an aspect to letterbox against, and the placeholder is a guess: a plain
     * 1 makes every media plane square, so a 5:3 poster whose texture 404s
     * renders as a square 60% of its own width and stays that way forever —
     * the node's authored box says landscape and nothing ever contradicted it.
     * Filling is also what the arriving texture will mostly do anyway, so the
     * plane no longer jumps outward on load.
     */
    protected updateQuadFit(): void {
        const quad = this.getFitQuad();
        if (!quad) return;
        const scaling = this.sceneObject?.transform?.scaling;
        const boxHeight = this.orientation === Orientation.Horizontal ? scaling?.z : scaling?.y;
        const boxAspect = scaling && boxHeight ? Math.abs(scaling.x / boxHeight) : 1;
        const rel = this.aspectRatio / (boxAspect || 1);
        const cover = this.aspectKnown && this.getFitMode() === 'cover';
        const sx = this.aspectKnown && !cover ? Math.min(1, rel) : 1;
        const sy = this.aspectKnown && !cover ? Math.min(1, 1 / rel) : 1;
        quad.scaling = new Vector3(sx, sy, 1);
        // Memo BEFORE applyQuadShape, for the same reason applyQuadShape
        // writes its own memos first: applyToMesh refreshes bounds, which
        // forces the parent world matrix and re-enters through the scale
        // watcher. The watcher's three-float guard reads this field, so a
        // stale value (null on the first fit) let the nested call run to
        // completion mid-write — and fire onQuadFitUpdated a second time,
        // so Video re-laid its controls twice per resize. Nothing between
        // here and the end of the method reads the previous value.
        if (scaling) this._fittedScale = scaling.clone();
        this.applyQuadShape(
            quad,
            Math.abs((scaling?.x ?? 1) * sx),
            Math.abs((boxHeight ?? 1) * sy),
            cover ? Math.min(1, 1 / rel) : 1,
            cover ? Math.min(1, rel) : 1,
        );
        this.onQuadFitUpdated(sx, sy);
        this.applyBackCard(quad);
        this.onFitChangedObservable.notifyObservers();
    }

    /** UV crop window currently applied to the quad (1, 1 = the full image). */
    private _cropWindow = { u: 1, v: 1 };

    /** Shape realized on the quad: null = the plain plane, else the rounding params. */
    private _shape: { radius: number; w: number; h: number } | null = null;

    /** Whether the realized geometry carries back faces (back: 'picture'). */
    private _backFaces = false;

    /** The color card behind the quad when back is 'color'; null otherwise. */
    private _backCard: Mesh | null = null;
    private _backCardMat: StandardMaterial | null = null;
    /** The `backColor` last written to the card: a refit with the same color leaves its material alone. */
    private _cardHex: string | null = null;
    /** Set when applyQuadShape rebuilds the quad's geometry; the card copies it. */
    private _cardGeometryDirty = false;

    /** Wire-data guard for `fit` — TS can't; a .trace file can carry anything. */
    protected validateFit(fit: unknown, errors: ValidationError[]): void {
        if (fit !== undefined && fit !== 'contain' && fit !== 'cover') {
            errors.push({ field: 'fit', message: `fit must be 'contain' or 'cover'`, code: 'INVALID_FIT' });
        }
    }

    /** Wire-data guard for `pill` — strict, so `"true"` fails rather than rounds. */
    protected validatePill(pill: unknown, errors: ValidationError[]): void {
        if (pill !== undefined && typeof pill !== 'boolean') {
            errors.push({ field: 'pill', message: 'pill must be a boolean', code: 'INVALID_PILL' });
        }
    }

    /** Wire-data guard for `back` - TS can't; a .trace file can carry anything. */
    protected validateBack(back: unknown, errors: ValidationError[]): void {
        if (back !== undefined && back !== 'none' && back !== 'picture' && back !== 'color') {
            errors.push({ field: 'back', message: `back must be 'none', 'picture' or 'color'`, code: 'INVALID_BACK' });
        }
    }

    /** Wire-data guard for `backColor` - #RRGGBB or #RRGGBBAA. */
    protected validateBackColor(color: unknown, errors: ValidationError[]): void {
        if (color !== undefined && (typeof color !== 'string' || !HEX_COLOR_RE.test(color))) {
            errors.push({ field: 'backColor', message: 'backColor must be #RRGGBB or #RRGGBBAA', code: 'INVALID_BACK_COLOR' });
        }
    }

    /**
     * Wire-data guard for `cornerRadius`: any non-negative metres value
     * (MeshBackground's bounds) — the render clamp handles oversize.
     */
    protected validateCornerRadius(radius: unknown, errors: ValidationError[]): void {
        if (radius !== undefined && (typeof radius !== 'number' || !Number.isFinite(radius) || radius < 0)) {
            errors.push({
                field: 'cornerRadius',
                message: 'cornerRadius must be a non-negative number (meters)',
                code: 'INVALID_RADIUS',
            });
        }
    }

    /**
     * Realize the quad's shape: rounded-corner geometry when the authored
     * radius asks for it, cover's crop window in the UVs.
     *
     * Both live in the mesh geometry, not texture uScale/vScale, for the
     * same reason setupQuad flips on geometry: any material must display it
     * correctly (Renderer material overrides bring their own textures), and
     * Video textures are cached per-URL across behaviors — a sampler
     * transform on the shared texture would leak one node's crop into
     * another's. Rounding reuses Bevel's generator (authored meters clamped
     * to half the smaller side, aspect-compensated arcs, world-radius
     * segment budget) against the media rect the fit produced, in the
     * rect's world dimensions so the corners stay circular however the box
     * is stretched.
     *
     * UVs are position-mapped into the centered window — u = u0 + (0.5 − x)
     * · window, the 0.5 − x being setupQuad's flip — one law that holds for
     * the 4-corner plane and every rounded outline, so flip, crop, and
     * rounding compose. Writes are skipped while shape, sides and window
     * are all unchanged: a plain quad (no cover, no radius, back not
     * `picture`) never touches the buffers setupQuad wrote; `picture`
     * rebuilds once to add the back faces.
     */
    private applyQuadShape(quad: Mesh, worldW: number, worldH: number, u: number, v: number): void {
        // `pill` is half the realized smaller side; an absolute radius clamps
        // to the same ceiling, so an oversized value degrades to a capsule
        // rather than self-crossing.
        //
        // PILL-CLAMP RULE (also in `computeBevelVertexData`, bevel.ts — grep
        // this marker to find both). This repeats that resolution on purpose:
        // the decision of whether to round AT ALL is made here, before the
        // geometry call, and it needs `half` to make it. Delegating would mean
        // calling the core to find out whether to call the core. If the rule
        // changes, both move together — they are the same two lines by
        // intent, not by accident.
        const half = Math.min(worldW, worldH) / 2;
        const authored = this.getPill() ? half : Math.max(this.getCornerRadius(), 0);
        const radius = Math.min(authored, half);
        // Same rounding threshold as computeBevelVertexData (`cornerWorld > 1e-7`):
        // a sub-tenth-micron corner is a plain quad at both sites.
        const rounded = radius > 1e-7 && worldW > 1e-6 && worldH > 1e-6;
        const s0 = this._shape;
        const shapeChanged = rounded
            ? !s0 || Math.abs(s0.radius - radius) > 1e-9
                || Math.abs(s0.w - worldW) > 1e-6 || Math.abs(s0.h - worldH) > 1e-6
            : s0 !== null;
        const backFaces = this.getBack() === 'picture';
        const sidesChanged = backFaces !== this._backFaces;
        const w0 = this._cropWindow;
        if (!shapeChanged && !sidesChanged && w0.u === u && w0.v === v) return;

        // Memos FIRST: applyToMesh refreshes bounds, which forces the parent
        // world matrix, which fires the scale watcher re-entrantly — the
        // nested updateQuadFit must see this shape as already applied.
        this._cropWindow = { u, v };
        if (shapeChanged || sidesChanged) {
            const single = rounded ? generateRoundedQuadGeometry(radius, [worldW, worldH]) : null;
            this._shape = single ? { radius, w: worldW, h: worldH } : null;
            this._backFaces = backFaces;
            this._cardGeometryDirty = true;
            const data = single ?? CreatePlaneVertexData({ size: 1 });
            (backFaces ? withBackFaces(data) : data).applyToMesh(quad);
        }

        const positions = quad.getVerticesData("position");
        const uvs = quad.getVerticesData("uv");
        if (!positions || !uvs) return;
        const u0 = (1 - u) / 2;
        const v0 = (1 - v) / 2;
        for (let i = 0, j = 0; j + 1 < uvs.length; i += 3, j += 2) {
            uvs[j] = u0 + (0.5 - positions[i]) * u;
            uvs[j + 1] = v0 + (0.5 - positions[i + 1]) * v;
        }
        quad.setVerticesData("uv", uvs);
    }

    /**
     * The `color` card: an unlit plate standing behind the quad, a child of
     * the quad so fit and aspect carry over, its geometry the quad's own
     * realized shape doubled - the color shows from behind AND through the
     * picture's transparent pixels from the front. The offset is half the
     * Frame plate's standing -0.01, so with a Frame present the order stays
     * picture, card, frame back - a framed picture's back is still the
     * frame's. (Behind is +z here: the quad's 180-degree Y turn flips it.)
     */
    private applyBackCard(quad: Mesh): void {
        if (this.getBack() !== 'color') {
            this.disposeBackCard();
            return;
        }
        if (!this._backCard || this._backCard.isDisposed()) {
            // Mesh.dispose leaves its material, so a card that went with a
            // disposed quad still has one to release.
            this.disposeBackCard();
            this._backCard = new Mesh(`${quad.name}_back`, quad.getScene());
            this._backCard.parent = quad;
            this._backCard.position.set(0, 0, 0.005);
            this._backCardMat = new StandardMaterial(`${quad.name}_back_mat`, quad.getScene());
            this._backCardMat.disableLighting = true;
            this._backCardMat.diffuseColor = Color3.Black();
            this._backCard.material = this._backCardMat;
            this._cardGeometryDirty = true;
            this.sceneObject.traceScene.refreshMeshPickability?.(this.sceneObject);
        }
        if (this._cardGeometryDirty) {
            const s = this._shape;
            const single = s ? generateRoundedQuadGeometry(s.radius, [s.w, s.h]) : null;
            withBackFaces(single ?? CreatePlaneVertexData({ size: 1 })).applyToMesh(this._backCard);
            this._cardGeometryDirty = false;
        }
        const hex = this.getBackColor();
        if (hex === this._cardHex) return;
        this._cardHex = hex;
        // Safe for FromHexString's parse-anything behavior: validateBackColor
        // holds the wire to HEX_COLOR_RE before a value ever lands here.
        const c = Color4.FromHexString(hex.length === 7 ? hex + 'FF' : hex);
        this._backCardMat!.emissiveColor = new Color3(c.r, c.g, c.b);
        this._backCardMat!.alpha = c.a;
        this._backCardMat!.transparencyMode = c.a < 1 ? Material.MATERIAL_ALPHABLEND : Material.MATERIAL_OPAQUE;
        // Drops a pre-pass the Renderer set while the card was translucent; a
        // card that stays translucent has it set again by the refresh below.
        this._backCardMat!.needDepthPrePass = false;
        // The writes above are the card's own look. Its alpha rides on the
        // mesh so a Renderer that attaches later multiplies opacity into it;
        // one already attached hears the notification and puts the object's
        // opacity and castShadows back over the writes.
        this._backCard.metadata = { ...(this._backCard.metadata as object | null), colorAlpha: c.a };
        this.sceneObject.onOwnedMeshesChangedObservable.notifyObservers();
    }

    /** Dispose the color card - pair with onDetach, like disposeFitWatcher. */
    protected disposeBackCard(): void {
        this._backCard?.dispose();
        this._backCard = null;
        this._backCardMat?.dispose();
        this._backCardMat = null;
        this._cardHex = null;
    }

    /**
     * Re-fit when the node's scale changes after the media loaded — a
     * texture that loads before a layout solve (or a gizmo resize)
     * otherwise keeps a contain-fit computed against the stale box.
     * World-matrix updates are frequent; the three-float compare gates the
     * actual work. Pair with disposeFitWatcher() in onDetach.
     */
    protected watchNodeScaleForFit(): void {
        if (this._fitObserver || !this.sceneObject?.transform) return;
        this._fitObserver = this.sceneObject.transform.onAfterWorldMatrixUpdateObservable.add((node) => {
            const s = node.scaling;
            const f = this._fittedScale;
            if (f && s.x === f.x && s.y === f.y && s.z === f.z) return;
            this.updateQuadFit();
        });
    }

    protected disposeFitWatcher(): void {
        if (this._fitObserver) {
            this._fitObserver.remove();
            this._fitObserver = null;
        }
        this._fittedScale = null;
        // A quad rebuilt on the next load starts from setupQuad's plain plane
        // and full-window UVs — the applied-shape memos must not claim a crop
        // or rounding it doesn't have.
        this._cropWindow = { u: 1, v: 1 };
        this._shape = null;
        this._backFaces = false;
    }
}
